import mime from "mime-types";

import { httpGet } from "../../../Commons/HttpServer/AttributeRouting/Methods";
import { NotFoundWebError } from "../../../Commons/HttpServer/AttributeRouting/NotFoundWebError";
import { httpType, httpUrlParam } from "../../../Commons/HttpServer/AttributeRouting/Params";
import { httpReturn } from "../../../Commons/HttpServer/AttributeRouting/Results";
import {
    StreamResult,
    StreamReturnResult,
} from "../../../Commons/HttpServer/AttributeRouting/Results/StreamResultHandler";
import { SingletonController } from "../../../Commons/HttpServer/ExpressWrapper/SingletonController";

import { EngineDevServer } from "./DevServers/EngineDevServer";

export class BuildServerLocalSourcesEngineController extends SingletonController {
    private readonly engineDirectory: string;
    private readonly devServers: Map<string, EngineDevServer> = new Map<string, EngineDevServer>();
    private portCounter = 14001;
    private readonly buildDebugOnly: boolean;

    public constructor(engineDirectory: string, buildDebugOnly: boolean) {
        super();
        this.engineDirectory = engineDirectory;
        this.buildDebugOnly = buildDebugOnly;
    }

    @httpGet(
        "/:update.hot-update.json",
        httpReturn.StreamWithMime("application/json"),
        httpUrlParam("update", httpType.String)
    )
    public async getDevServerUpdateJson(updateName: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`${updateName}.hot-update.json`);
    }

    @httpGet(
        "/:update.hot-update.js",
        httpReturn.StreamWithMime("application/json"),
        httpUrlParam("update", httpType.String)
    )
    public async getDevServerUpdateJs(updateName: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`${updateName}.hot-update.js`);
    }

    @httpGet("/public/scripts/candy_local.js", httpReturn.StreamWithMime("application/javascript"))
    public async getLocalEngineScript(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.js");
    }

    @httpGet(
        "/public/scripts/:hash.skbkontur.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineSkbkonturScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.skbkontur.js`);
    }

    @httpGet(
        "/public/scripts/:hash.skbkontur.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineSkbkonturMapScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.skbkontur.js.map`);
    }

    @httpGet(
        "/public/scripts/:hash.skbkontur.min.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineSkbkonturMinScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.skbkontur.min.js`);
    }

    @httpGet(
        "/public/scripts/:hash.skbkontur.min.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineSkbkonturMinMapScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.skbkontur.min.js.map`);
    }

    @httpGet(
        "/public/scripts/:hash.vendor.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineVendorScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.vendor.js`);
    }

    @httpGet(
        "/public/scripts/:hash.vendor.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineVendorMapScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.vendor.js.map`);
    }

    @httpGet(
        "/public/scripts/:hash.vendor.min.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineVendorMinScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.vendor.min.js`);
    }

    @httpGet(
        "/public/scripts/:hash.vendor.min.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getEngineVendorMinMapScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.vendor.min.js.map`);
    }

    @httpGet(
        "/public/scripts/:hash.runtime.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getWebpackRuntimeScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.runtime.js`);
    }

    @httpGet(
        "/public/scripts/:hash.runtime.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getWebpackRuntimeMapScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.runtime.js.map`);
    }

    @httpGet(
        "/public/scripts/:hash.runtime.min.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getWebpackRuntimeMinScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.runtime.min.js`);
    }

    @httpGet(
        "/public/scripts/:hash.runtime.min.js.map",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("hash", httpType.String)
    )
    public async getWebpackRuntimeMinMapScript(hash: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${hash}.runtime.min.js.map`);
    }

    @httpGet("/public/scripts/engineLoader_local.js", httpReturn.StreamWithMime("application/javascript"))
    public async getEngineLoaderScript(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/engineLoader_local.js");
    }

    @httpGet("/public/scripts/engineLoader_local.min.js", httpReturn.StreamWithMime("application/javascript"))
    public async getEngineLoaderMinScript(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/engineLoader_local.min.js");
    }

    @httpGet(
        "/public/scripts/:chunkname.candy_local.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("chunkname", httpType.String)
    )
    public async getLocalEngineChunk(chunkname: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${chunkname}.candy_local.js`);
    }

    @httpGet("/public/styles/candy_local.css", httpReturn.StreamWithMime("text/css"))
    public async getLocalEngineStyles(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/styles/candy_local.css");
    }

    @httpGet("/public/scripts/candy_local.min.js", httpReturn.StreamWithMime("application/javascript"))
    public async getLocalEngineScriptMinified(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.min.js");
    }

    @httpGet(
        "/public/scripts/:chunkname.candy_local.min.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("chunkname", httpType.String)
    )
    public async getLocalEngineChunkMinified(chunkname: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${chunkname}.candy_local.min.js`);
    }

    @httpGet("/public/styles/candy_local.min.css", httpReturn.StreamWithMime("text/css"))
    public async getLocalEngineStylesMinified(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/styles/candy_local.min.css");
    }

    @httpGet("/public/scripts/candy_local.js.map", httpReturn.StreamWithMime("application/octet-stream"))
    public async getLocalEngineScriptSourceMap(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.js.map");
    }

    @httpGet(
        "/public/scripts/:chunkname.candy_local.js.map",
        httpReturn.StreamWithMime("application/octet-stream"),
        httpUrlParam("chunkname", httpType.String)
    )
    public async getLocalEngineChunkSourceMap(chunkname: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/${chunkname}.candy_local.js.map`);
    }

    @httpGet("/public/styles/candy_local.css.map", httpReturn.StreamWithMime("application/octet-stream"))
    public async getLocalEngineStylesSourceMap(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/styles/candy_local.css.map");
    }

    @httpGet("/public/scripts/candy_local.min.js.map", httpReturn.StreamWithMime("application/octet-stream"))
    public async getLocalEngineScriptMinifiedSourceMap(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.min.js.map");
    }

    @httpGet("/public/styles/candy_local.min.css.map", httpReturn.StreamWithMime("application/octet-stream"))
    public async getLocalEngineStylesMinifiedSourceMap(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/styles/candy_local.min.css.map");
    }

    @httpGet(
        "/public/scripts/candy_local.:theme.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineScript(): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.js");
    }

    @httpGet(
        "/public/styles/candy_local.:theme.css",
        httpReturn.StreamWithMime("text/css"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineStyles(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/styles/candy_local.${theme}.css`, theme);
    }

    @httpGet(
        "/public/scripts/candy_local.:theme.min.js",
        httpReturn.StreamWithMime("application/javascript"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineScriptMinified(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.min.js", theme);
    }

    @httpGet(
        "/public/styles/candy_local.:theme.min.css",
        httpReturn.StreamWithMime("text/css"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineStylesMinified(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/styles/candy_local.${theme}.min.css`, theme);
    }

    @httpGet(
        "/public/scripts/candy_local.:theme.js.map",
        httpReturn.StreamWithMime("application/octet-stream"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineScriptSourceMap(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream("public/scripts/candy_local.js.map", theme);
    }

    @httpGet(
        "/public/styles/candy_local.:theme.css.map",
        httpReturn.StreamWithMime("application/octet-stream"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineStylesSourceMap(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/styles/candy_local.${theme}.css.map`, theme);
    }

    @httpGet(
        "/public/scripts/candy_local.:theme.min.js.map",
        httpReturn.StreamWithMime("application/octet-stream"),
        httpUrlParam("theme", httpType.String)
    )
    public async getThemedLocalEngineScriptMinifiedSourceMap(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/scripts/candy_local.${theme}.min.js.map`, theme);
    }

    @httpGet("/public/styles/candy_local.:theme.min.css.map", httpReturn.StreamWithMime("application/octet-stream"))
    public async getThemedLocalEngineStylesMinifiedSourceMap(theme: string): Promise<StreamReturnResult> {
        return this.getFileAsStream(`public/styles/candy_local.${theme}.min.css.map`, theme);
    }

    @httpGet("/public/fonts/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getFormFont(fileName: string): Promise<StreamReturnResult> {
        try {
            const stream = await this.getFileAsStream(`public/fonts/${fileName}`);
            return new StreamResult(stream, mime.lookup(fileName));
        } catch {
            throw new NotFoundWebError("File not found", true);
        }
    }

    @httpGet("/public/images/:filename", httpReturn.Stream, httpUrlParam("filename", httpType.String))
    public async getFormImages(fileName: string): Promise<StreamReturnResult> {
        try {
            const stream = await this.getFileAsStream(`public/images/${fileName}`);
            return new StreamResult(stream, mime.lookup(fileName));
        } catch {
            throw new NotFoundWebError("File not found", true);
        }
    }

    private async getFileAsStream(name: string, theme?: string): Promise<NodeJS.ReadableStream> {
        return (await this.getOrCreateDevServer(theme)).getFileAsStream(name);
    }

    private async getOrCreateDevServer(theme: undefined | string): Promise<EngineDevServer> {
        let devServer = this.devServers.get(theme ?? "Default");
        if (devServer == undefined) {
            devServer = new EngineDevServer(this.portCounter, this.engineDirectory, this.buildDebugOnly, theme);
            this.portCounter++;
            this.devServers.set(theme ?? "Default", devServer);
            await devServer.start();
            return devServer;
        }
        return devServer;
    }
}
